import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { tableCellClasses } from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useCaseloadReportQuery } from '../../app/services/account';
import { useUpdatePatientFlagMutation } from '../../app/services/patient';
import { useToast } from '../../app/toast';
import CustomPagination from '../../common/components/CustomPagination';
import DataList from '../../common/components/DataList';
import DataTable from '../../common/components/DataTable';
import Loading from '../../common/components/Loading';
import PopupModal from '../../common/components/PopupModal';
import RoundedContainer from '../../common/components/RoundedContainer';
import CaseloadReportIcon, {
  CaseloadReportDefinitions,
} from '../../common/components/icons/CaseloadReportIcon';
import DeletePatientIcon from '../../common/components/icons/DeletePatientIcon';
import EditPatientIcon from '../../common/components/icons/EditPatientIcon';
import ProfileIcon from '../../common/components/icons/ProfileIcon';
import {
  selectContentPopup,
  selectIsOpenPopup,
  setContentPopup,
  setIsOpenPopup,
} from '../../common/slices/globalSlice';
import { DefaultPageSize, truncateText } from '../../common/utils/commonUtils';
import { PageSource } from '../../common/utils/pageInfo';
import { canChangeFlagPatient, isAuditor } from '../../common/utils/permissionUtils';
import { PopupContent } from '../../types/PopupType';
import { selectUser } from '../auth/authSlice';
import CaseloadReportSortMenu from '../patient/CaseloadReportSortMenu';
import ContextMenu from '../patient/ContextMenu';

interface CaseloadReportData {
  patientId: string;
  patientName: string;
  firstName?: string;
  lastName?: string;
  externalId?: string;
  patientFlag: number;
  clinicSite: string;
  status: string;
  billingAlertColor: string;
  billingAlert: number;
  screened: number;
  isScreenedCompleted: boolean;
  symptoms: number;
  functionality: number;
  phq9: number;
  gad7: number;
  interview: number;
  diagnosis: number;
}

const StyledDataCell = styled('p')(() => ({
  margin: 0,
  fontSize: '12px',
}));

const StyledSortWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  margin: '12px 0px',
  width: '100%',
  padding: '0px 24px',
  columnGap: '8px',
  [theme.breakpoints.up('md')]: {
    marginTop: 0,
    width: 'auto',
    flexBasis: '40%',
    maxWidth: '1200px',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '16px',
    padding: '0px 16px',
  },
}));

const StyledItem = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
  fontSize: '12px',
}));

const StyledPhoneItem = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
  fontSize: '16px',
}));

export default function CaseloadReportPage() {
  const toast = useToast();
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const shouldRenderDesktopData = useMediaQuery(theme.breakpoints.up('md'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tableRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [displayedPatientData, setDisplayedPatientData] = useState<CaseloadReportData[]>([]);
  const [patientActionId, setPatientActionId] = useState<string>('');
  const currentUser = useAppSelector(selectUser);
  const isOpenPopup = useAppSelector(selectIsOpenPopup);
  const contentPopup = useAppSelector(selectContentPopup);

  const canViewCaseloadReport = currentUser.canViewCaseloadReport;
  const [searchOptions, setSearchOptions] = useState<any>({
    page: searchParams.get('page') || '1',
    sortField: searchParams.get('sort') || 'patientFlag',
    sortDir: searchParams.get('dir') || 'asc',
  });

  const removeSuicideContentPopup = {
    title: t('patient.caseloadReport.removeSuicideRisk.popup.title'),
    description: t('patient.caseloadReport.removeSuicideRisk.popup.description'),
    btnOk: t('patient.caseloadReport.removeSuicideRisk.popup.btnOk'),
    btnClose: t('patient.caseloadReport.removeSuicideRisk.popup.btnClose'),
  } as PopupContent;

  const currentPage = parseInt(searchOptions.page);

  // Query data
  const {
    data: patientData,
    isLoading: isLoadingPatient,
    refetch: handleRefetchData,
  } = useCaseloadReportQuery(
    canViewCaseloadReport
      ? {
          search: searchOptions.search,
          page: currentPage,
          pageSize: DefaultPageSize,
          sort: searchOptions.sortField + ' ' + searchOptions.sortDir,
          status: searchOptions.status,
        }
      : skipToken
  );

  const [updateFlag] = useUpdatePatientFlagMutation();

  const isLoading = isLoadingPatient;
  const showPagingInfo = totalPages > 1;
  const getDisplayedPatientName = React.useCallback((patient: CaseloadReportData) => {
    const patientNames = [];
    if (!(patient.firstName?.length || patient.lastName?.length)) {
      if (patient.externalId) {
        return patient.externalId;
      } else {
        return patient.patientId;
      }
    }
    if (patient.lastName) {
      patientNames.push(patient.lastName);
    }
    if (patient.firstName) {
      patientNames.push(patient.firstName);
    }
    return patientNames.join(', ');
  }, []);

  useEffect(() => {
    if (patientData?.caseLoadReportData) {
      const displayedData = (patientData?.caseLoadReportData || []).map(
        (item: CaseloadReportData) => {
          return {
            ...item,
            patientName: getDisplayedPatientName(item),
          };
        }
      );

      setDisplayedPatientData(displayedData);
      setTotalPages(Math.ceil(patientData.totalItems / DefaultPageSize));
    }
  }, [patientData, getDisplayedPatientName]);

  const updateSearchOptions = (updatedData: any) => {
    const params = {
      ...searchOptions,
      ...updatedData,
    };
    setSearchOptions(params);
    handleRefetchData();
  };

  const handlePageChange = (value: any) => {
    const newPage = parseInt(value);
    updateSearchOptions({ page: newPage });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemoveSuicideRisk = async (successCallback?: () => void) => {
    try {
      await updateFlag({ patientId: patientActionId }).unwrap();
      handleRefetchData();
      if (successCallback) {
        successCallback();
      }
      toast.publish(t('patient.caseloadReport.removeSuicideRisk.toastMessageSuccess'), 'success');
    } catch (e) {
      toast.publish(t('patient.caseloadReport.removeSuicideRisk.toastMessageFail'), 'error');
    }
  };

  const handleTableSorting = (sortField: string) => {
    if (sortField === searchOptions.sortField) {
      const newDir = searchOptions.sortDir === 'asc' ? 'desc' : 'asc';
      updateSearchOptions({ sortDir: newDir });
    } else {
      updateSearchOptions({ sortField, sortDir: sortField === 'patientFlag' ? 'desc' : 'asc' });
    }
  };

  const showRemoveSuicidePopup = async (patientId: string) => {
    dispatch(setContentPopup({ content: removeSuicideContentPopup }));
    dispatch(setIsOpenPopup({ value: true }));
    setPatientActionId(patientId);
    handleClose();
  };

  const handleMenuSorting = (sortField: string, sortDir: string) => {
    updateSearchOptions({ sortField, sortDir });
  };

  const renderContextMenu = (item: CaseloadReportData) => {
    return (
      <ContextMenu
        id={`patient-${item.patientId}`}
        anchorEl={anchorEl}
        handleClick={handleClick}
        handleClose={handleClose}
        menuItems={[
          {
            label: t('patient.caseloadReport.editPatient'),
            isDisplayed: !isAuditor(currentUser.roleId),
            icon: <EditPatientIcon />,
            onClick: () => {
              navigate(
                `/dashboard/edit-patient/` + item.patientId + `?src=${PageSource.PatientList}`,
                { state: {} }
              );
            },
          },
          {
            label: t('patient.caseloadReport.viewPatient'),
            icon: <ProfileIcon />,
            onClick: () => {
              navigate(`/dashboard/patient/` + item.patientId, { state: {} });
            },
          },
          {
            isDisplayed:
              canChangeFlagPatient(currentUser.roleId) &&
              item.patientFlag === CaseloadReportDefinitions.Red_Flag,
            label: t('patient.caseloadReport.removeSuicideRisk.title'),
            icon: <DeletePatientIcon />,
            onClick: () => {
              showRemoveSuicidePopup(item.patientId);
            },
          },
        ]}
      />
    );
  };

  const renderDesktopData = (data: CaseloadReportData[]) => {
    return (
      <DataTable
        sort={{ field: searchOptions.sortField, dir: searchOptions.sortDir }}
        onSorted={handleTableSorting}
        commonCellStyles={{
          [`&.${tableCellClasses.head}`]: {
            padding: '16px 8px',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
            fontSize: '12px',
          },
          [`&.${tableCellClasses.body}`]: {
            padding: '16px 8px',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
          },
        }}
        headers={[
          {
            text: t('patient.caseloadReport.patientName'),
            sx: { width: '24%' },
            sortKey: 'patientFlag',
          },
          { text: t('patient.caseloadReport.site'), sx: { width: '19%' }, sortKey: 'site' },
          {
            text: t('patient.caseloadReport.billingAlert'),
            sx: { width: '12%' },
            sortKey: 'billingAlert',
          },
          {
            text: t('patient.caseloadReport.status'),
            sx: { textAlign: 'center', width: '5%' },
            sortKey: 'status',
          },
          {
            text: t('patient.caseloadReport.screened'),
            sx: { textAlign: 'center', width: '10%' },
            sortKey: 'screened',
          },
          {
            text: t('patient.caseloadReport.symptoms'),
            sx: { textAlign: 'center', width: '14%' },
            sortKey: 'symptoms',
          },
          {
            text: t('patient.caseloadReport.functionality'),
            sx: { textAlign: 'center', width: '10%' },
            sortKey: 'functionality',
          },
          {
            text: t('patient.caseloadReport.phq9'),
            sx: { textAlign: 'center', width: '10%', whiteSpace: 'nowrap' },
            sortKey: 'phq9',
          },
          {
            text: t('patient.caseloadReport.gad7'),
            sx: { textAlign: 'center', width: '10%', whiteSpace: 'nowrap' },
            sortKey: 'gad7',
          },
          {
            text: t('patient.caseloadReport.interview'),
            sx: { textAlign: 'center', width: '10%' },
            sortKey: 'interview',
          },
          {
            text: t('patient.caseloadReport.diagnosis'),
            sx: { textAlign: 'center', width: '10%' },
            sortKey: 'diagnosis',
          },
          {
            text: t('patient.caseloadReport.action'),
            sx: { textAlign: 'center', width: '10%' },
          },
        ]}
        items={data.map((item) => {
          let screened: React.ReactElement = <StyledDataCell></StyledDataCell>;
          if (item.isScreenedCompleted) {
            screened = <StyledDataCell>{item.screened}</StyledDataCell>;
          } else {
            screened = (
              <StyledItem>
                <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.screened} />
              </StyledItem>
            );
          }
          let billingAlert: React.ReactElement = <StyledDataCell></StyledDataCell>;
          if (!item.billingAlertColor && item.billingAlert) {
            billingAlert = (
              <StyledDataCell>
                <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.billingAlert} />
              </StyledDataCell>
            );
          }
          if (item.billingAlertColor && item.billingAlert > 0) {
            billingAlert = (
              <StyledDataCell sx={{ color: item.billingAlertColor }}>
                {`${item.billingAlert} more minutes`}
              </StyledDataCell>
            );
          }
          return [
            {
              component: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.patientFlag} />
                  <Link href={`/dashboard/patient/${item.patientId}`}>
                    <StyledDataCell>{item.patientName}</StyledDataCell>
                  </Link>
                </Box>
              ),
            },
            {
              component: (
                <Tooltip title={item.clinicSite} arrow>
                  <StyledDataCell>{truncateText(item.clinicSite)}</StyledDataCell>
                </Tooltip>
              ),
            },
            {
              component: billingAlert,
              sx: { textAlign: 'center' },
            },
            {
              component: <StyledDataCell>{item.status}</StyledDataCell>,
              sx: { textAlign: 'center' },
            },
            {
              component: screened,
              sx: { textAlign: 'center' },
            },
            {
              component: (
                <StyledItem>
                  <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.symptoms} />
                </StyledItem>
              ),
            },
            {
              component: (
                <StyledItem>
                  <CaseloadReportIcon
                    isDesktop={shouldRenderDesktopData}
                    type={item.functionality}
                  />
                </StyledItem>
              ),
            },
            {
              component: (
                <StyledItem>
                  <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.phq9} />
                </StyledItem>
              ),
            },
            {
              component: (
                <StyledItem>
                  <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.gad7} />
                </StyledItem>
              ),
            },
            {
              component: (
                <StyledItem>
                  <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.interview} />
                </StyledItem>
              ),
            },
            {
              component: (
                <StyledItem>
                  <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.diagnosis} />
                </StyledItem>
              ),
            },
            {
              component: renderContextMenu(item),
              sx: { textAlign: 'center' },
            },
          ];
        })}
      />
    );
  };

  const listItemsFilterPhone = (item: CaseloadReportData) => {
    let screened: React.ReactElement;
    if (item.isScreenedCompleted) {
      screened = <StyledPhoneItem>{item.screened}</StyledPhoneItem>;
    } else {
      screened = (
        <StyledPhoneItem>
          <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.screened} />
        </StyledPhoneItem>
      );
    }
    let billingAlert: React.ReactElement = <StyledPhoneItem></StyledPhoneItem>;
    if (!item.billingAlertColor && item.billingAlert) {
      billingAlert = (
        <StyledPhoneItem>
          <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.billingAlert} />
        </StyledPhoneItem>
      );
    }
    if (item.billingAlertColor && item.billingAlert > 0) {
      billingAlert = (
        <StyledPhoneItem sx={{ color: item.billingAlertColor }}>
          {`${item.billingAlert} more minutes`}
        </StyledPhoneItem>
      );
    }
    return [
      [
        t('patient.caseloadReport.patientId'),
        <Link href={`/dashboard/patient/${item.patientId}`}>
          {item.externalId ? item.externalId : item.patientId}
        </Link>,
      ],
      [t('patient.caseloadReport.site'), item.clinicSite],
      [t('patient.caseloadReport.billingAlert'), billingAlert, { textAlign: 'center' }],
      [t('patient.caseloadReport.status'), item.status],
      [t('patient.caseloadReport.screened'), screened],
      [
        t('patient.caseloadReport.symptoms'),
        <StyledItem>
          <CaseloadReportIcon type={item.symptoms} />
        </StyledItem>,
      ],
      [
        t('patient.caseloadReport.functionality'),
        <StyledItem>
          <CaseloadReportIcon type={item.functionality} />
        </StyledItem>,
      ],
      [
        t('patient.caseloadReport.phq9'),
        <StyledItem>
          <CaseloadReportIcon type={item.phq9} />
        </StyledItem>,
      ],
      [
        t('patient.caseloadReport.gad7'),
        <StyledItem>
          <CaseloadReportIcon type={item.gad7} />
        </StyledItem>,
      ],
      [
        t('patient.caseloadReport.interview'),
        <StyledItem>
          <CaseloadReportIcon type={item.interview} />
        </StyledItem>,
      ],
      [
        t('patient.caseloadReport.diagnosis'),
        <StyledItem>
          <CaseloadReportIcon type={item.diagnosis} />
        </StyledItem>,
      ],
    ];
  };
  const renderPhoneData = (data: CaseloadReportData[]) => {
    return (
      <DataList
        lineHeight={'auto'}
        data={data.map((item) => ({
          leftHeader: (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CaseloadReportIcon isDesktop={shouldRenderDesktopData} type={item.patientFlag} />
              <Link href={`/dashboard/patient/${item.patientId}`}>
                {item.patientName && item.patientName.length > 60
                  ? item.patientName.substr(0, 60) + '...'
                  : item.patientName}
              </Link>
            </Box>
          ),
          rightHeader: renderContextMenu(item),
          items: listItemsFilterPhone(item),
        }))}
      />
    );
  };

  return (
    <>
      <Box
        sx={{
          [theme.breakpoints.up('md')]: {
            display: 'flex',
            justifyContent: 'space-between',
          },
        }}
        ref={tableRef}
      >
        <StyledSortWrapper>
          {!shouldRenderDesktopData && (
            <CaseloadReportSortMenu
              sx={{ width: '20%', flexShrink: 0, borderRadius: '8px', minWidth: 'unset' }}
              sortField={searchOptions.sortField}
              sortDir={searchOptions.sortDir}
              onSorted={handleMenuSorting}
            />
          )}
        </StyledSortWrapper>
      </Box>

      {isLoading ? (
        <Loading />
      ) : (
        <Box ref={tableRef} sx={{ mt: -1 }}>
          {displayedPatientData && (
            <>
              <Box sx={{ width: '100%' }}>
                {displayedPatientData.length === 0 ? (
                  <RoundedContainer sx={{ mt: 2, p: 2 }}>
                    <Typography>{t('patient.caseloadReport.dataNotFound')}</Typography>
                  </RoundedContainer>
                ) : (
                  <>
                    {shouldRenderDesktopData
                      ? renderDesktopData(displayedPatientData)
                      : renderPhoneData(displayedPatientData)}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: showPagingInfo ? 4 : 2,
                        borderBottomLeftRadius: '15px',
                        borderBottomRightRadius: '15px',
                        background: theme.xPalette.white,
                      }}
                    >
                      {showPagingInfo && (
                        <CustomPagination
                          changePage={(e, value) => handlePageChange(value)}
                          currentPage={currentPage}
                          pages={totalPages}
                          pageRef={tableRef}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}
          <PopupModal
            isOpen={isOpenPopup}
            contentPopup={contentPopup}
            onClick={handleRemoveSuicideRisk}
          />
        </Box>
      )}
    </>
  );
}
